import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { RefusalNotice, useWritesRefused } from '../components/SuspendedNotice.js';
import { Alert, Button, Card, TextField } from '../components/ui.js';
import { ApiError } from '../lib/api.js';
import { API_URL } from '../lib/config.js';
import { formatCredits, formatDateTime } from '../lib/format.js';
import {
  creditsStateExplanation,
  isExtensionOrder,
  orderTypeLabel,
  statusExplanation,
  statusLabel,
  statusToneClasses,
} from '../lib/orderDisplay.js';
import {
  cancelOrder,
  deliverableDownloadUrl,
  fetchOrder,
  isIntakeV2,
  type Deliverable,
  type IntakeLink,
  type Order,
  type OrderDetail,
  type OrderIntakeSnapshot,
  type Site,
  type TimelineEvent,
} from '../lib/ordersApi.js';
import { useSession } from '../session/SessionProvider.js';

function formatSize(bytes: number | null): string {
  if (bytes === null) return '';
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function Timeline({ events }: { events: TimelineEvent[] }): React.JSX.Element {
  return (
    <ol className="mt-4 space-y-4">
      {events.map((event, index) => (
        <li key={event.id} className="flex gap-4">
          <div className="flex flex-col items-center">
            <span
              className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${
                index === events.length - 1 ? 'bg-blue-ink' : 'bg-rule-strong'
              }`}
            />
            {index < events.length - 1 ? <span className="w-px flex-1 bg-rule" /> : null}
          </div>
          <div className="pb-1">
            <p className="text-sm font-medium text-fg">{statusLabel(event.toStatus)}</p>
            <p className="text-sm text-fg-muted">{formatDateTime(event.createdAt)}</p>
            {event.note ? <p className="mt-1 text-sm text-fg-2">{event.note}</p> : null}
          </div>
        </li>
      ))}
    </ol>
  );
}

function Deliverables({
  orderId,
  deliverables,
}: {
  orderId: string;
  deliverables: Deliverable[];
}): React.JSX.Element {
  if (deliverables.length === 0) {
    return (
      <p className="mt-3 text-sm text-fg-muted">
        Nothing to download yet. Reports appear here once the site is live.
      </p>
    );
  }

  return (
    <ul className="mt-4 divide-y divide-rule-soft">
      {deliverables.map((deliverable) => (
        <li key={deliverable.id} className="flex items-center gap-4 py-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-fg">{deliverable.label}</p>
            <p className="text-sm text-fg-muted">
              {deliverable.filename ?? 'File'}
              {deliverable.sizeBytes ? ` · ${formatSize(deliverable.sizeBytes)}` : ''}
            </p>
          </div>
          {/* A plain link to the authenticated API route: the browser sends the
              session cookie, and there is no shareable URL to leak. */}
          <a
            href={`${API_URL}${deliverableDownloadUrl(orderId, deliverable.id)}`}
            className="shrink-0 text-sm font-medium text-fg underline"
          >
            Download
          </a>
        </li>
      ))}
    </ul>
  );
}

export function OrderDetailPage(): React.JSX.Element {
  const { orderId } = useParams<{ orderId: string }>();
  const [detail, setDetail] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orderId) return;
    let cancelled = false;
    void (async () => {
      try {
        const response = await fetchOrder(orderId);
        if (!cancelled) setDetail(response);
      } catch {
        if (!cancelled) setError('We could not find that order.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  if (loading) {
    return (
      <p className="text-sm text-fg-muted" role="status">
        Loading…
      </p>
    );
  }

  if (error || !detail) {
    return <Alert>{error ?? 'We could not find that order.'}</Alert>;
  }

  const { order, timeline, deliverables, site } = detail;
  /**
   * A renewal has no build, no review and nothing to deliver (ruling C-4), so
   * the Downloads card and its "reports appear here once the site is live"
   * copy do not belong on one. There is nothing coming.
   */
  const isRenewal = isExtensionOrder(order.orderType);

  return (
    <div className="space-y-6">
      <div>
        <Link to="/orders" className="text-sm text-fg-muted underline">
          Back to orders
        </Link>
        <div className="mt-2 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              {orderTypeLabel(order.orderType)}
            </h1>
            <p className="mt-1 text-sm text-fg-muted">
              {statusExplanation(order.status, order.orderType)}
            </p>
          </div>
          <span
            className={`shrink-0 rounded-full px-3 py-1 text-sm font-medium ${statusToneClasses(
              order.status,
            )}`}
          >
            {statusLabel(order.status)}
          </span>
        </div>
      </div>

      {order.years > 1 ? (
        <Card title="What this covers">
          <p className="mt-3 text-sm text-fg">
            {isRenewal
              ? `${order.years} more years of this site being live.`
              : `The build, plus ${order.years} years of the site being live.`}
          </p>
        </Card>
      ) : null}

      {/*
        The handover - D-14 Item 9.3.
        Above Credits, because the day a build lands this is the only thing on
        this page anybody opened it for.
      */}
      {site && site.liveUrl ? <HandoverCard site={site} /> : null}

      <Card title="Credits">
        <p className="mt-3 text-sm text-fg">
          {creditsStateExplanation(order.creditsState, order.creditsCost)}
        </p>
        {order.creditsState === 'reserved' ? (
          <p className="mt-2 text-sm text-fg-muted">
            {formatCredits(order.creditsCost)} will be taken from your balance when{' '}
            {isRenewal ? 'the extension is applied' : 'the site goes live'}.
          </p>
        ) : null}
      </Card>

      <LinksCard snapshot={order.intakeSnapshot} />

      <Card title="Progress">
        <Timeline events={timeline} />
      </Card>

      {/*
        **Craig's ruling R1, 23 September 2026.** The card showed "Nothing to
        download yet. Reports appear here once the site is live." on every
        order, which is a promise the portal does not yet keep: white-label
        reporting is not built, and the Your site card above already carries the
        handover. It appears when there is something in it.
      */}
      {isRenewal || deliverables.length === 0 ? null : (
        <Card title="Downloads">
          <Deliverables orderId={order.id} deliverables={deliverables} />
        </Card>
      )}

      {order.operatorNote ? (
        <Card title="Note from the team">
          <p className="mt-3 text-sm whitespace-pre-line text-fg">{order.operatorNote}</p>
        </Card>
      ) : null}

      {order.status === 'submitted' ? (
        <CancelOrder
          orderId={order.id}
          creditsCost={order.creditsCost}
          onCancelled={(next) => setDetail({ ...detail, order: next })}
        />
      ) : null}
    </div>
  );
}

/**
 * The links the agency chose, read back - C-57, pre-launch fixes.
 *
 * The buyer picks where the home page link points and what every link says,
 * and until now could not see any of it once the order was placed: the operator
 * page showed it, and the person who typed it had nowhere to check. This reads
 * the **frozen intake snapshot**, the copy taken at submit, so editing the
 * project afterwards cannot make this page disagree with what is being built.
 *
 * Labelled as the order form labels them, so the words on the form and the
 * words here are the same words. Read-only: a change is a conversation with us,
 * not an edit. An order with no link rows - a renewal, a round-1 order, a
 * mentions-only build - shows nothing at all.
 */
function LinksCard({
  snapshot,
}: {
  snapshot: OrderIntakeSnapshot | null;
}): React.JSX.Element | null {
  if (!snapshot || !isIntakeV2(snapshot) || snapshot.links.length === 0) return null;

  // Home first, whatever order the snapshot holds them in.
  const links: IntakeLink[] = [
    ...snapshot.links.filter((link) => link.role === 'home'),
    ...snapshot.links.filter((link) => link.role !== 'home'),
  ];

  return (
    <Card title="Links">
      <ul className="mt-4 space-y-4">
        {links.map((link, index) => (
          <li key={`${link.role}-${String(index)}`} className="rounded-box bg-subtle p-3">
            <p className="text-xs font-medium font-mono tracking-wide text-fg-2 uppercase">
              {link.role === 'home' ? 'Home page link' : 'Another link'}
            </p>
            <dl className="mt-2 grid gap-3 sm:grid-cols-2">
              <div>
                <dt className="text-xs font-medium text-fg-2">Target URL</dt>
                <dd className="mt-0.5 text-sm break-all text-fg">{link.url}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-fg-2">Anchor text</dt>
                <dd className="mt-0.5 text-sm text-fg">
                  {link.wording ?? (
                    <span className="text-fg-muted">
                      None given, so we use the name: {snapshot.targetName}
                    </span>
                  )}
                </dd>
              </div>
            </dl>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/**
 * Cancelling is available at `submitted` and nowhere else (ruling O-1). Once a
 * build is under way the server refuses, so the control simply is not offered.
 */
function CancelOrder({
  orderId,
  creditsCost,
  onCancelled,
}: {
  orderId: string;
  creditsCost: number;
  onCancelled: (order: Order) => void;
}): React.JSX.Element {
  const { refresh } = useSession();
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
   * **WAIT-B round 2, defect 3.** The customer's own cancel was the one write
   * P-4 never reached, because it is not a buy control and the sweep was
   * looking for those.
   *
   * `'active-only'`, the default, and both reasons are real here.
   * `POST /api/orders/:id/cancel` carries `requireAgencyState('active')`:
   * cancelling releases credits onto an account whose credits are about to be
   * valued and refunded, so a suspended agency is refused and the operator
   * cancels from the offboarding panel instead.
   */
  const { refused } = useWritesRefused();

  async function handleCancel(): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      const response = await cancelOrder(orderId, reason.trim() || undefined);
      onCancelled(response.order);
      await refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'We could not cancel this order.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Cancel this order">
      {error ? (
        <div className="mt-3">
          <Alert>{error}</Alert>
        </div>
      ) : null}

      <p className="mt-3 text-sm text-fg-2">
        We have not started work yet, so you can cancel this yourself. Your{' '}
        {formatCredits(creditsCost)} go straight back to your balance.
      </p>

      {refused ? (
        <div className="mt-4">
          <RefusalNotice action="cancel an order" />
        </div>
      ) : confirming ? (
        <div className="mt-4 space-y-3">
          <TextField
            label="Why are you cancelling?"
            hint="Optional, but it helps us."
            name="reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          <div className="flex items-center gap-3">
            <Button variant="secondary" busy={busy} onClick={() => void handleCancel()}>
              Yes, cancel this order
            </Button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="text-sm text-fg-muted underline"
            >
              Keep the order
            </button>
          </div>
        </div>
      ) : (
        <Button variant="secondary" className="mt-4" onClick={() => setConfirming(true)}>
          Cancel this order
        </Button>
      )}
    </Card>
  );
}

/**
 * What was delivered, as links - D-14 Item 9.3.
 *
 * **The reports promised on the website do not exist yet**, so until they do
 * this is the handover: where the site is, and which pages carry the client's
 * links. Shown only once there is a live URL, because a site row with nothing
 * on it is a card that raises a question rather than answering one.
 *
 * Sites delivered before migration 0018 have no recorded link URLs. The list
 * simply does not appear for them, which is the truth about those orders rather
 * than an error worth explaining on screen.
 */
function HandoverCard({ site }: { site: Site }): React.JSX.Element {
  return (
    <Card title="Your site">
      <p className="mt-3 text-sm text-fg">
        <a
          href={site.liveUrl ?? undefined}
          target="_blank"
          rel="noreferrer"
          className="font-medium underline"
        >
          {site.liveUrl}
        </a>
      </p>

      {site.linkUrls.length > 0 ? (
        <div className="mt-4">
          <p className="text-sm text-fg-2">Your client&rsquo;s links are on these pages:</p>
          <ul className="mt-2 space-y-1">
            {site.linkUrls.map((url) => (
              <li key={url} className="text-sm">
                <a href={url} target="_blank" rel="noreferrer" className="break-all underline">
                  {url}
                </a>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Card>
  );
}
